<template>
    <div class="p-4 bg-gray-50 rounded-lg border">
        <!-- 🗓️ Header -->
        <div class="flex items-center justify-between mb-4">
            <h2 class="text-xl font-bold">
                {{ prettyDate }}
            </h2>

            <span class="text-sm text-gray-500">
                {{ interventionsCount }} {{ interventionsCount > 1 ? 'interventions' : 'intervention' }}
            </span>
        </div>

        <!-- 🗓️ Calendrier -->
        <div ref="dp" class="rounded border bg-white shadow" style="height: 400px;"></div>
    </div>
</template>

<script>
import { DayPilot } from "daypilot-pro-javascript";

export default {
    name: "PlanningDay",

    props: {
        date: {
            type: String,
            required: true
        },
        events: {
            type: Array,
            required: true
        }
    },

    data() {
        return {
            calendar: null
        };
    },

    computed: {
        prettyDate() {
            return new DayPilot.Date(this.date).toString("dddd dd MMMM yyyy", "fr-fr");
        },

        openingHour() {
            return this.timeToDecimal(
                this.$account.account.societe.heure_ouverture
            );
        },

        closingHour() {
            return this.timeToDecimal(
                this.$account.account.societe.heure_fermeture
            );
        },

        interventionsCount() {
            if (!Array.isArray(this.events)) return 0;
            return this.events.filter(e => e && e.type === "intervention").length;
        }
    },

    watch: {
        date() {
            this.refresh();
        },

        events: {
            handler() {
                this.refresh();
            },
            deep: true
        }
    },

    methods: {
        refresh() {
            if (!this.calendar) return;

            this.calendar.update({
                startDate: this.date,
                events: this.buildEvents()
            });
        },

        normalizeTime(time) {
            if (!time) return "00:00:00";
            return time.length === 5 ? time + ":00" : time;
        },

        normalizeDate(date) {
            if (!date) return null;
            return String(date).split("T")[0];
        },

        timeToDecimal(time) {
            if (!time) return 0;

            const [hours, minutes = 0] = String(time).split(":").map(Number);
            return hours + minutes / 60;
        },

        buildAdresseLabel(adresse) {
            if (!adresse) return "Adresse inconnue";

            const cp = adresse.code_postal || adresse.cp || "";
            const ville = adresse.ville || "";
            const a1 = adresse.adresse1 || "";

            const cpVille = `${cp} ${ville}`.trim();
            if (cpVille) return cpVille;
            if (a1) return a1;

            return "Adresse inconnue";
        },

        buildDayPilotEvent(payload) {
            return {
                id: payload.id,
                text: payload.text,
                start: payload.start,
                end: payload.end,
                backColor: payload.backColor,
                borderColor: payload.borderColor,
                fontColor: payload.fontColor,
                moveDisabled: payload.moveDisabled || false,
                resizeDisabled: payload.resizeDisabled || false,
                clickDisabled: payload.clickDisabled || false,
                data: payload.data
            };
        },

        buildEvents() {
            const list = Array.isArray(this.events) ? this.events : [];
            const out = [];

            list.forEach((e, index) => {
                if (!e || !e.type) return;

                // On n'affiche que les events calés sur la journée + avec heures
                if (!e.date || !e.heure_debut || !e.heure_fin) return;

                const date = this.normalizeDate(e.date);

                // Sécurité : si ce n'est pas la date du composant, on ignore
                if (date !== this.normalizeDate(this.date)) return;

                const startIso = `${date}T${this.normalizeTime(e.heure_debut)}`;
                const endIso = `${date}T${this.normalizeTime(e.heure_fin)}`;

                const start = new DayPilot.Date(startIso);
                const end = new DayPilot.Date(endIso);

                // Trajet
                if (e.type === "trajet") {
                    if (!e.temps_trajet_min || Number(e.temps_trajet_min) <= 0) return;

                    const distanceKm = e.distance_km != null ? Number(e.distance_km) : null;
                    const distanceLabel = distanceKm != null && !Number.isNaN(distanceKm) ? ` • ${distanceKm} km` : "";

                    out.push(this.buildDayPilotEvent({
                        id: `trajet-${date}-${index}`,
                        text: `[TRAJET] ${Number(e.temps_trajet_min)} min${distanceLabel} - ${this.normalizeTime(e.heure_debut).slice(0, 5)} → ${this.normalizeTime(e.heure_fin).slice(0, 5)}`,
                        start,
                        end,
                        backColor: "#e5e7eb",
                        borderColor: "#F4F4F4",
                        fontColor: "#6b7280",
                        moveDisabled: true,
                        resizeDisabled: true,
                        clickDisabled: true,
                        data: e
                    }));

                    return;
                }

                // Intervention
                if (e.type === "intervention") {
                    const isNew = Number(e.new_event) === 1;

                    const adresseLabel = this.buildAdresseLabel(e.adresse || e.intervention?.adresse);

                    const reference = e.intervention?.reference || e.reference || "";
                    const title = reference ? `${reference}` : "Intervention";

                    out.push(this.buildDayPilotEvent({
                        id: `intervention-${e.id || e.intervention_id || index}`,
                        text: `[${adresseLabel}] ${title}\n${this.normalizeTime(e.heure_debut).slice(0, 5)} → ${this.normalizeTime(e.heure_fin).slice(0, 5)}`,
                        start,
                        end,
                        backColor: isNew ? "#22c55e" : "#4781de",
                        borderColor: isNew ? "#16a34a" : "#3563aa",
                        fontColor: "white",
                        moveDisabled: !isNew,
                        resizeDisabled: true,
                        clickDisabled: false,
                        data: e
                    }));

                    return;
                }

                // depart / arrivee : non affichés en bloc (tu peux les gérer plus tard si besoin)
            });

            return out;
        },

        initCalendar() {
            if (this.calendar) return;

            this.calendar = new DayPilot.Calendar(this.$refs.dp, {
                viewType: "Day",
                timeFormat: "Clock24Hours",
                locale: "fr-fr",

                // 🔧 précision planning
                cellDuration: 5,
                cellHeight: 16,
                snapToGrid: false,

                startDate: this.date,
                durationBarVisible: true,
                dayBeginsHour: this.openingHour,
                dayEndsHour: this.closingHour,
                showNonBusiness: false,

                eventOverlapHandling: "Allow",
                eventArrangement: "Cascade",

                timeRangeSelectedHandling: "Disabled",
                eventMoveHandling: "Update",
                eventResizeHandling: "Disabled",
                eventClickHandling: "Bubble",

                onEventClicked: args => {
                    const data = args && args.e ? args.e.data : null;
                    if (!data) return;
                    if (data.type === "trajet") return;
                    if (Number(data.new_event) === 1) return;
                    this.$emit("click-event", data);
                },

                onEventMoved: args => {
                    const data = args.e.data;
                    if (!data) return;

                    // On ne gère que le new_event
                    if (Number(data.data.new_event) !== 1) return;

                    const start = args.newStart.toString("HH:mm:ss");
                    const end = args.newEnd.toString("HH:mm:ss");

                    this.$emit("move", {
                        heure_debut: start,
                        heure_fin: end,
                        event: data
                    });
                },
            });

            this.calendar.init();
            this.refresh();
        }
    },

    mounted() {
        this.initCalendar();
    }
};
</script>

<style scoped>
/* Optionnel : ajustement esthétique */
</style>
